/**
 * JxWindowEvents — single shared pubsub channel for all JxForm-chain window/panel events
 * (drag, resize, etc), fired BY the JxForm window itself, not by engines.
 *
 * Lives in JxWindowManager/ — JxWindowEngine subscribes here to keep its cache up to date,
 * and JxFormDraggableBase (in JxForms/) publishes here on drag-end. See services/wss/events/
 * ComponentEvents.js for the engine-to-UI equivalent; keep these separate, different
 * direction of communication (that one is engine -> UI, this one is window -> anyone listening,
 * including JxWindowEngine itself).
 *
 * WHY ONE SHARED CHANNEL instead of a per-instance topic string (e.g. `${getId()}.OnPosChanged`):
 * a listener subscribes ONCE to `JxWindowEvents.channel` and gets every window event from
 * every JxForm instance, filtering on payload.target itself. No need to know in advance
 * which window ids exist, or re-subscribe whenever a new dialog opens.
 *
 * WHY PUBSUB AT ALL (instead of e.g. an onSavePos prop on JxForm):
 * JxFormDraggableBase fires these fire-and-forget — it does not know or care whether
 * anything is listening, or what a listener does with the data (write to localStorage,
 * debounce + send to BE, ignore it entirely). That decision lives entirely with whoever
 * subscribes, not with the base class. See Jerny.Development note "Drag Handler — 3
 * mogelijke implementaties" (rk=0f693d83ea2d42ddaa580851f042b609) for the full reasoning.
 *
 * HOW TO USE:
 *   publish:   publish(JxWindowEvents.channel, { name: JxWindowEvents.onPosChanged, target: this.getId(), data: { posX, posY } });
 *   subscribe: subscribe(JxWindowEvents.channel, ({ name, target, data }) => { ... filter on name/target yourself ... });
 *
 * Payload shape: { name, target, data }
 *   - name:   which event, see names below
 *   - target: this.getId() of the JxForm window that fired it
 *   - data:   event-specific payload, shape depends on `name`
 */
export const JxWindowEvents = {
    /**
     * Root pubsub channel for all JxForm window events. Subscribe here, then filter on
     * payload.name and payload.target — do not invent per-instance topic strings.
     */
    channel: 'OnJxWindowEvent',

    /**
     * Fired by JxFormDraggableBase on drag-end (mouseup after a drag).
     * data: { posX: number, posY: number } — the window's new top-left position in px.
     * Consumed by JxWindowEngine, which writes it into JxWindowCache and (later) persists
     * it to the BE.
     */
    onPosChanged: 'OnPosChanged',

    /**
     * Fired by JxFormDraggableBase on mousedown (titlebar click or panel click).
     * Consumed by JxWindowEngine, which assigns the highest zIndex to the selected window
     * and notifies all JxForm instances via ComponentNotifier so they can update their own zIndex.
     * data: null
     */
    onWindowSelected: 'OnWindowSelected',

    /**
     * Reserved for a future resize mixin — not fired yet anywhere.
     * Intended data shape once implemented: { width: number, height: number }.
     */
    onSizeChanged: 'OnSizeChanged',
};
